import { useCallback, useRef, useState } from 'react';
import type { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import type { ChartDrillLevel, ChartTimeframeGrain, CrewingTabKey } from './crewingAnalyticsStaticData';
import {
  fetchCrewingAdoptionFunnel,
  fetchCrewingAdoptionTrend,
  fetchCrewingDirectIndirectHours,
  fetchCrewingEmployeeTable,
  fetchCrewingHoursCategoryTable,
  fetchCrewingKcphPerformance,
  fetchCrewingKcphTrend,
  fetchCrewingKpiOverview,
  fetchCrewingLocationTable,
  fetchCrewingOtherFixedHours,
  fetchCrewingQualityMetrics,
  fetchCrewingQuadrant,
  fetchCrewingScheduleHealth,
  fetchCrewingSchedulingTrend,
  fetchCrewingSubmissionDetails,
  fetchCrewingToolAdoption,
  fetchCrewingVarianceHeatmap,
  type AdoptionFunnelResponse,
  type AdoptionTrendResponse,
  type CrewingFilterRequest,
  type CrewingKpiMetric,
  type HoursCategoryRow,
  type HoursGroup,
  type KcphPerformanceRow,
  type KcphPerformanceResponse,
  type KcphTrendResponse,
  type LocationEfficiencyRow,
  type QualityMetricsRow,
  type QuadrantPoint,
  type ScheduleHealthRow,
  type SchedulingTrendResponse,
  type SubmissionDetailsResponse,
  type ToolAdoptionRow,
  type VarianceHeatmapResponse,
  type EmployeeEfficiencyRow,
  type PagedResponse,
  type LocationTableResponse,
  type HoursCategoryTableResponse,
  type QualityMetricsTableResponse,
} from './crewingAnalyticsApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AsyncState<T> = { data: T | null; loading: boolean; error: string | null };

const idle = <T>(): AsyncState<T> => ({ data: null, loading: false, error: null });
const loading = <T>(): AsyncState<T> => ({ data: null, loading: true, error: null });
const ok = <T>(data: T): AsyncState<T> => ({ data, loading: false, error: null });
const err = <T>(error: string): AsyncState<T> => ({ data: null, loading: false, error });

// Per the updated backend contract, the 4 grouped-table endpoints (location-table, category-table,
// quality-metrics, submission-details) now do REAL server-side pagination: `total` is the full
// filtered grouped-row count and `totals` is the aggregate across the full filtered result set,
// independent of the requested page — the UI must render `totals` as-is (never recompute/merge it
// client-side) and request one page of rows at a time. 25 matches the backend's own default.
const DEFAULT_TABLE_PAGE_SIZE = 25;
// User-selectable page sizes for the 4 grouped tables' pager.
export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// ─── Real server-side pagination for the grouped tables ─────────────────────
// The 4 grouped-table endpoints, plus (as of the latest backend contract) the Employee Level
// Efficiency drilldown (/efficiency/employee-table, which now also supports page/pageSize/sortBy/
// sortDirection and returns a real `total`), all fetch exactly ONE page at a time and render the
// response as-is — no more client-side accumulation/streaming across pages.
export type GroupedTableField = 'locationTable' | 'hoursCategoryTable' | 'qualityMetrics' | 'submissionDetails' | 'employeeTable';

export interface TableSortState {
  sortBy: string | null;
  sortDirection: 'asc' | 'desc';
}

const DEFAULT_TABLE_SORT: TableSortState = { sortBy: null, sortDirection: 'asc' };
// Employee Level Efficiency's pre-existing UX default (was a client-side sort before the backend
// supported sortBy) — preserved by sending it explicitly on the first fetch for a newly-selected
// location, instead of leaving sortBy unset (which would fall back to the backend's own default,
// employeeName ASC).
const EMPLOYEE_TABLE_DEFAULT_SORT: TableSortState = { sortBy: 'actualKcph', sortDirection: 'desc' };

// Dimension `sortBy` keys allowed per grouped table (per backend contract) — used to detect when a
// groupDimensions change (from the column picker) invalidates the currently-active sort column.
const SORT_DIMENSION_KEYS: Record<GroupedTableField, string[]> = {
  locationTable: ['region', 'market', 'location', 'year', 'period', 'week', 'category', 'zone'],
  hoursCategoryTable: ['region', 'market', 'location', 'year', 'period', 'week', 'category', 'zone', 'size'],
  qualityMetrics: ['region', 'market', 'location', 'year', 'period', 'week', 'category', 'zone'],
  submissionDetails: ['region', 'market', 'location', 'year', 'period', 'week'],
  // No column picker/groupDimensions for this table — never consulted by refetchGroupedTable.
  employeeTable: ['location', 'employeeName', 'employeeGpid', 'role', 'year', 'period', 'week', 'category', 'zone'],
};

// hours/category-table only: metric `sortBy` keys that are only present in the response (and thus
// only sortable) when 'location' is part of the active groupDimensions.
const HOURS_CATEGORY_LOCATION_ONLY_SORT_KEYS = ['actualCases', 'routes', 'siteSqft'];


export interface CrewingApiData {
  kpiOverview:        AsyncState<CrewingKpiMetric[]>;
  scheduleHealth:     AsyncState<ScheduleHealthRow[]>;
  schedulingTrend:    AsyncState<SchedulingTrendResponse>;
  kcphPerformance:    AsyncState<KcphPerformanceResponse>;
  toolAdoption:       AsyncState<ToolAdoptionRow[]>;
  // "vs prior" comparison data — same shape as their live counterparts, fetched for the prior
  // period (or prior year, when the chart timeframe grain is 'year') for the same grouping/filters.
  // priorKpiOverview exists because the backend's own kpiOverview.delta/trendDirection turned out
  // to be (value - target), not a real prior-period comparison — so the UI computes its own delta
  // from this instead of trusting the backend fields for the 7 top KPI tiles.
  priorKpiOverview:     AsyncState<CrewingKpiMetric[]>;
  priorScheduleHealth:  AsyncState<ScheduleHealthRow[]>;
  priorKcphPerformance: AsyncState<KcphPerformanceResponse>;
  priorToolAdoption:    AsyncState<ToolAdoptionRow[]>;
  varianceHeatmap:    AsyncState<VarianceHeatmapResponse>;
  kcphTrend:          AsyncState<KcphTrendResponse>;
  locationTable:      AsyncState<LocationTableResponse>;
  employeeTable:      AsyncState<PagedResponse<EmployeeEfficiencyRow>>;
  directIndirect:     AsyncState<HoursGroup[]>;
  otherFixed:         AsyncState<HoursGroup[]>;
  hoursCategoryTable: AsyncState<HoursCategoryTableResponse>;
  adoptionFunnel:     AsyncState<AdoptionFunnelResponse>;
  quadrant:           AsyncState<QuadrantPoint[]>;
  adoptionTrend:      AsyncState<AdoptionTrendResponse>;
  qualityMetrics:     AsyncState<QualityMetricsTableResponse>;
  submissionDetails:  AsyncState<SubmissionDetailsResponse>;
}

const emptyData = (): CrewingApiData => ({
  kpiOverview:        idle(),
  scheduleHealth:     idle(),
  schedulingTrend:    idle(),
  kcphPerformance:    idle(),
  toolAdoption:       idle(),
  priorScheduleHealth:  idle(),
  priorKcphPerformance: idle(),
  priorToolAdoption:    idle(),
  priorKpiOverview:     idle(),
  varianceHeatmap:    idle(),
  kcphTrend:          idle(),
  locationTable:      idle(),
  employeeTable:      idle(),
  directIndirect:     idle(),
  otherFixed:         idle(),
  hoursCategoryTable: idle(),
  adoptionFunnel:     idle(),
  quadrant:           idle(),
  adoptionTrend:      idle(),
  qualityMetrics:     idle(),
  submissionDetails:  idle(),
});

// Which apiData fields each tab needs — only these are fetched when that tab is loaded
const TAB_FIELDS: Record<CrewingTabKey, (keyof CrewingApiData)[]> = {
  'executive-overview':   ['kpiOverview', 'scheduleHealth', 'schedulingTrend', 'kcphPerformance', 'toolAdoption', 'priorScheduleHealth', 'priorKcphPerformance', 'priorToolAdoption', 'priorKpiOverview'],
  'efficiency-breakdown': ['varianceHeatmap', 'kcphTrend', 'locationTable', 'employeeTable'],
  'hours-breakdown':      ['directIndirect', 'otherFixed', 'hoursCategoryTable'],
  'crewing-execution':    ['adoptionFunnel', 'quadrant', 'adoptionTrend', 'qualityMetrics', 'submissionDetails'],
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseCrewingAnalyticsOptions {
  selectedRegions:    KPIFilterDropdownItem[];
  selectedMarkets:    KPIFilterDropdownItem[];
  selectedLocations:  KPIFilterDropdownItem[];
  selectedYears:      KPIFilterDropdownItem[];
  selectedPeriods:    KPIFilterDropdownItem[];
  selectedWeeks:      KPIFilterDropdownItem[];
  selectedZones:      KPIFilterDropdownItem[];
  selectedCategories: KPIFilterDropdownItem[];
  chartHierarchyLevel: ChartDrillLevel;
  chartTimeframeGrain: ChartTimeframeGrain;
  // Currently-selected column/groupDimensions for each grouped table — needed so the initial/Apply/Reset
  // fetch (not just the column-picker's refetchGroupedTable) sends the right groupDimensions.
  locationTableColumns?:      string[];
  hoursCategoryTableColumns?: string[];
  qualityMetricsTableColumns?: string[];
  submissionDetailsTableColumns?: string[];
}

export function useCrewingAnalytics(opts: UseCrewingAnalyticsOptions) {
  const [data, setData] = useState<CrewingApiData>(emptyData);
  // Avoid stale closures when opts change between calls
  const optsRef = useRef(opts);
  optsRef.current = opts;
  // Tracks which tabs have already been fetched for the currently-applied filters
  const fetchedTabsRef = useRef<Set<CrewingTabKey>>(new Set());
  // Current zero-based page for each of the 4 grouped tables — reset to 0 whenever
  // filters/groupDimensions/page size change so a stale page number is never carried over.
  const [tablePages, setTablePages] = useState<Record<GroupedTableField, number>>({
    locationTable: 0,
    hoursCategoryTable: 0,
    qualityMetrics: 0,
    submissionDetails: 0,
    employeeTable: 0,
  });
  // User-selectable rows-per-page for each of the 4 grouped tables — independent per table since
  // a user reviewing Submission Details in bulk may still want small pages on Quality Metrics.
  const [tablePageSizes, setTablePageSizes] = useState<Record<GroupedTableField, number>>({
    locationTable: DEFAULT_TABLE_PAGE_SIZE,
    hoursCategoryTable: DEFAULT_TABLE_PAGE_SIZE,
    qualityMetrics: DEFAULT_TABLE_PAGE_SIZE,
    submissionDetails: DEFAULT_TABLE_PAGE_SIZE,
    employeeTable: DEFAULT_TABLE_PAGE_SIZE,
  });
  // Prevent a slower, superseded page/column request from replacing newer rows or totals.
  const groupedTableRequestRef = useRef<Record<GroupedTableField, number>>({
    locationTable: 0,
    hoursCategoryTable: 0,
    qualityMetrics: 0,
    submissionDetails: 0,
    employeeTable: 0,
  });
  // Current sort column/direction for each of the 4 grouped tables — unset (sortBy: null) by
  // default so the initial/Apply/Reset fetch preserves the backend's default grouping order.
  const [tableSort, setTableSort] = useState<Record<GroupedTableField, TableSortState>>({
    locationTable: { ...DEFAULT_TABLE_SORT },
    hoursCategoryTable: { ...DEFAULT_TABLE_SORT },
    qualityMetrics: { ...DEFAULT_TABLE_SORT },
    submissionDetails: { ...DEFAULT_TABLE_SORT },
    employeeTable: { ...EMPLOYEE_TABLE_DEFAULT_SORT },
  });
  // Which location the Employee Level Efficiency drilldown is currently scoped to — `employeeTable`
  // always filters on a single Location_ID, independent of (and not reflected in) the global
  // location filter, so pager/sort clicks need this remembered separately to re-apply it.
  const employeeTableLocationRef = useRef<string | null>(null);

  const columnsForField = (field: GroupedTableField): string[] | undefined => {
    const o = optsRef.current;
    if (field === 'locationTable') return o.locationTableColumns;
    if (field === 'hoursCategoryTable') return o.hoursCategoryTableColumns;
    if (field === 'qualityMetrics') return o.qualityMetricsTableColumns;
    if (field === 'submissionDetails') return o.submissionDetailsTableColumns;
    return undefined; // employeeTable has no groupDimensions/column-picker concept
  };

  const buildRequest = useCallback((overrides?: Partial<CrewingFilterRequest>): CrewingFilterRequest => {
    const o = optsRef.current;
    const str = (items: KPIFilterDropdownItem[]) =>
      items.map((i) => String(i.id)).filter(Boolean);
    // Strip 'P'/'W' prefixes (P1→1, W3→3) and pure numerics both work
    const numericId = (raw: string) => Number(raw.replace(/^[A-Za-z]+/, ''));
    const nums = (items: KPIFilterDropdownItem[]) =>
      items.map((i) => numericId(String(i.id))).filter((n) => !isNaN(n) && n > 0);

    // The backend expects "weeks" as week-IN-PERIOD (1-4), paired with "periods" — same
    // convention as Perfect Truck Analytics — not the raw week-of-year number encoded in the
    // dropdown's id/displayText ("W29 (P8W1)" → weekInPeriod 1, period 8). Sending the raw
    // week-of-year value here silently returns no rows once a period is also selected.
    let periods = nums(o.selectedPeriods);
    let weeks: number[] = [];
    if (o.selectedWeeks.length > 0) {
      const periodsFromWeeks = new Set<number>();
      const weekInPeriodValues: number[] = [];
      o.selectedWeeks.forEach((w) => {
        const match = String(w.displayText).match(/\(P(\d+)W(\d+)\)/);
        if (match) {
          periodsFromWeeks.add(Number(match[1]));
          weekInPeriodValues.push(Number(match[2]));
        }
      });
      if (weekInPeriodValues.length > 0) {
        weeks = weekInPeriodValues;
        periods = Array.from(new Set([...periods, ...periodsFromWeeks]));
      }
    }

    return {
      regions:    str(o.selectedRegions),
      markets:    str(o.selectedMarkets),
      locations:  str(o.selectedLocations),
      years:      nums(o.selectedYears),
      periods,
      weeks,
      zones:      str(o.selectedZones),
      categories: str(o.selectedCategories),
      groupBy:    o.chartHierarchyLevel,
      // Backend now supports 'day' directly — pass the selected grain through unchanged.
      timeGrain:  o.chartTimeframeGrain,
      // pageSize 100000 502'd even with groupDimensions removed, confirming the backend itself
      // can't handle a large single page — 200 is confirmed-working default for the
      // non-paginated endpoints (charts/KPIs). The 4 grouped tables + employeeTable override
      // this via fetchAllPages(), which pages through in chunks of 5000 instead.
      page:       0,
      pageSize:   200,
      ...overrides,
    };
  }, []);

  // Derives the "prior" comparison request from the current one: prior YEAR when the chart
  // timeframe grain is 'year' (each selected year - 1), otherwise prior PERIOD/WEEK (each
  // selected period/week - 1, rolling back into the previous year when at period 1 / week 1).
  // 'day' has no dedicated filter of its own — it only changes chart aggregation granularity —
  // so it shifts the same way 'week' does (falling back to period-only shift when no weeks
  // are selected).
  const buildPriorRequest = useCallback((): CrewingFilterRequest => {
    const req = buildRequest();
    if (req.timeGrain === 'year') {
      return { ...req, years: (req.years ?? []).map((y) => y - 1) };
    }
    if ((req.timeGrain === 'week' || req.timeGrain === 'day') && (req.weeks?.length ?? 0) > 0) {
      // weeks are week-IN-PERIOD (1-4) — rolling back past week 1 must also decrement the
      // associated period (and roll period 1 → 13 with a year rollback), not wrap at 52.
      const years = req.years ?? [];
      const periods = req.periods ?? [];
      const hasWeekOne = (req.weeks ?? []).some((w) => w <= 1);
      const weeks = (req.weeks ?? []).map((w) => (w > 1 ? w - 1 : 4));
      const rolledPeriods = hasWeekOne ? periods.map((p) => (p > 1 ? p - 1 : 13)) : periods;
      const rolledYears = hasWeekOne && periods.some((p) => p <= 1) ? years.map((y) => y - 1) : years;
      return { ...req, years: rolledYears, periods: rolledPeriods, weeks };
    }
    if ((req.periods?.length ?? 0) > 0) {
      const years = req.years ?? [];
      const periods = (req.periods ?? []).map((p) => (p > 1 ? p - 1 : 13));
      const rolledYears = (req.periods ?? []).some((p) => p <= 1) ? years.map((y) => y - 1) : years;
      return { ...req, years: rolledYears, periods };
    }
    // No period/week selected to shift — fall back to prior year so there's still a comparison baseline.
    return { ...req, years: (req.years ?? []).map((y) => y - 1) };
  }, [buildRequest]);

  // Fetches exactly one page of a grouped table and replaces that field's state wholesale —
  // `total`/`totals` come straight from the response, so grand totals are always the
  // full-filtered-scope numbers the backend computed, never a client-side sum of visible rows.
  const fetchGroupedTablePage = useCallback(async (
    field: GroupedTableField,
    page: number,
    groupDimensionsOverride?: string[],
    pageSizeOverride?: number,
    sortOverride?: TableSortState,
  ) => {
    const requestId = groupedTableRequestRef.current[field] + 1;
    groupedTableRequestRef.current[field] = requestId;
    const pageSize = pageSizeOverride ?? tablePageSizes[field];
    const effectiveSort = sortOverride ?? tableSort[field];
    const req = buildRequest({
      page,
      pageSize,
      groupDimensions: groupDimensionsOverride ?? columnsForField(field),
      // Omit sortBy/sortDirection entirely when unset so the request matches the default/initial
      // shape (no sort fields) and the backend falls back to its default grouping order.
      ...(effectiveSort.sortBy
        ? { sortBy: effectiveSort.sortBy, sortDirection: effectiveSort.sortDirection }
        : {}),
      // employeeTable always scopes to a single Location_ID independent of the global location
      // filter — re-applied here so pager/sort clicks (which don't pass this explicitly) still work.
      ...(field === 'employeeTable' && employeeTableLocationRef.current
        ? { locations: [employeeTableLocationRef.current] }
        : {}),
    });
    setTablePages((prev) => ({ ...prev, [field]: page }));
    if (pageSizeOverride !== undefined) {
      setTablePageSizes((prev) => ({ ...prev, [field]: pageSizeOverride }));
    }
    if (sortOverride) {
      setTableSort((prev) => ({ ...prev, [field]: sortOverride }));
    }
    // Keep the current page and backend totals visible while the next page loads. Clearing the
    // response here made grand totals disappear or appear to change during pagination.
    setData((prev) => ({
      ...prev,
      [field]: {
        ...(prev[field] as AsyncState<any>),
        loading: true,
        error: null,
      },
    }));
    try {
      const fetcher =
        field === 'locationTable' ? fetchCrewingLocationTable :
        field === 'hoursCategoryTable' ? fetchCrewingHoursCategoryTable :
        field === 'qualityMetrics' ? fetchCrewingQualityMetrics :
        field === 'submissionDetails' ? fetchCrewingSubmissionDetails :
        fetchCrewingEmployeeTable;
      const result = await fetcher(req);
      if (groupedTableRequestRef.current[field] !== requestId) return;
      setData((prev) => ({ ...prev, [field]: ok(result) }));
    } catch (e: any) {
      if (groupedTableRequestRef.current[field] !== requestId) return;
      setData((prev) => ({ ...prev, [field]: err(e?.message ?? 'Failed to load') }));
    }
  }, [buildRequest, tablePageSizes, tableSort]);

  // Called by the table's Prev/Next pager — keeps the currently-applied groupDimensions/page size/sort.
  const goToTablePage = useCallback((field: GroupedTableField, page: number) => {
    fetchGroupedTablePage(field, Math.max(0, page));
  }, [fetchGroupedTablePage]);

  // Called by the table's rows-per-page control — always resets back to page 0, since the
  // previously-viewed page number generally no longer lines up with the new page size.
  const setTablePageSize = useCallback((field: GroupedTableField, pageSize: number) => {
    fetchGroupedTablePage(field, 0, undefined, pageSize);
  }, [fetchGroupedTablePage]);

  // Called by a sortable column header click — toggles asc/desc on the same column, or resets to
  // asc on a newly-clicked column. Always re-fetches page 0 since the row order is changing.
  const toggleTableSort = useCallback((field: GroupedTableField, columnKey: string) => {
    const current = tableSort[field];
    const next: TableSortState = current.sortBy === columnKey
      ? { sortBy: columnKey, sortDirection: current.sortDirection === 'asc' ? 'desc' : 'asc' }
      : { sortBy: columnKey, sortDirection: 'asc' };
    fetchGroupedTablePage(field, 0, undefined, undefined, next);
  }, [tableSort, fetchGroupedTablePage]);

  const fetchTab = useCallback(async (tab: CrewingTabKey) => {
    const req = buildRequest();
    const priorReq = buildPriorRequest();
    // Prior-period comparisons only make sense against a single year/period/week — with more
    // than one of any selected, there's no single "prior" bucket to compare against, so skip
    // the extra API calls entirely and let the 'vs prior' UI stay hidden.
    const o = optsRef.current;
    const canShowPrior = o.selectedYears.length <= 1 && o.selectedPeriods.length <= 1 && o.selectedWeeks.length <= 1;
    const singleLocation = req.locations?.length === 1 ? req.locations : undefined;
    const fields = TAB_FIELDS[tab];

    const safe = async <T>(
      fetcher: () => Promise<T>,
      key: keyof CrewingApiData,
    ): Promise<[keyof CrewingApiData, AsyncState<T>]> => {
      try {
        const result = await fetcher();
        return [key, ok(result)];
      } catch (e: any) {
        return [key, err<T>(e?.message ?? 'Failed to load')];
      }
    };

    // Only fields that have a fetcher for the current filters (e.g. employeeTable needs a single location)
    const fetchers: Partial<Record<keyof CrewingApiData, () => Promise<[keyof CrewingApiData, AsyncState<any>]>>> = {
      kpiOverview:        () => safe(() => fetchCrewingKpiOverview(req).then((r) => r.metrics), 'kpiOverview'),
      priorKpiOverview:   () => canShowPrior ? safe(() => fetchCrewingKpiOverview(priorReq).then((r) => r.metrics), 'priorKpiOverview') : Promise.resolve(['priorKpiOverview', ok([])] as [keyof CrewingApiData, AsyncState<any>]),
      scheduleHealth:     () => safe(() => fetchCrewingScheduleHealth(req).then((r) => r.rows), 'scheduleHealth'),
      schedulingTrend:    () => safe(() => fetchCrewingSchedulingTrend(req), 'schedulingTrend'),
      kcphPerformance:    () => safe(() => fetchCrewingKcphPerformance(req), 'kcphPerformance'),
      toolAdoption:       () => safe(() => fetchCrewingToolAdoption(req).then((r) => r.rows), 'toolAdoption'),
      priorScheduleHealth:  () => canShowPrior ? safe(() => fetchCrewingScheduleHealth(priorReq).then((r) => r.rows), 'priorScheduleHealth') : Promise.resolve(['priorScheduleHealth', ok([])] as [keyof CrewingApiData, AsyncState<any>]),
      priorKcphPerformance: () => canShowPrior ? safe(() => fetchCrewingKcphPerformance(priorReq), 'priorKcphPerformance') : Promise.resolve(['priorKcphPerformance', ok({ rows: [] })] as [keyof CrewingApiData, AsyncState<any>]),
      priorToolAdoption:    () => canShowPrior ? safe(() => fetchCrewingToolAdoption(priorReq).then((r) => r.rows), 'priorToolAdoption') : Promise.resolve(['priorToolAdoption', ok([])] as [keyof CrewingApiData, AsyncState<any>]),
      varianceHeatmap:    () => safe(() => fetchCrewingVarianceHeatmap(req), 'varianceHeatmap'),
      kcphTrend:          () => safe(() => fetchCrewingKcphTrend(req), 'kcphTrend'),
      directIndirect:     () => safe(() => fetchCrewingDirectIndirectHours(req).then((r) => r.groups), 'directIndirect'),
      otherFixed:         () => safe(() => fetchCrewingOtherFixedHours(req).then((r) => r.groups), 'otherFixed'),
      adoptionFunnel:     () => safe(() => fetchCrewingAdoptionFunnel(req), 'adoptionFunnel'),
      quadrant:           () => safe(() => fetchCrewingQuadrant(req).then((r) => r.points), 'quadrant'),
      adoptionTrend:      () => safe(() => fetchCrewingAdoptionTrend(req), 'adoptionTrend'),
    };

    // The 4 grouped tables (plus employeeTable, when a single location is resolved) now fetch a
    // single page (page 0, reset on every tab load/Apply/Reset) instead of streaming every page —
    // `fetchGroupedTablePage` updates `data`/`tablePages` itself.
    const paginatedFetchers: Partial<Record<keyof CrewingApiData, () => Promise<void>>> = {
      locationTable:      () => fetchGroupedTablePage('locationTable', 0),
      hoursCategoryTable: () => fetchGroupedTablePage('hoursCategoryTable', 0),
      qualityMetrics:     () => fetchGroupedTablePage('qualityMetrics', 0),
      submissionDetails:  () => fetchGroupedTablePage('submissionDetails', 0),
      ...(singleLocation
        ? { employeeTable: () => { employeeTableLocationRef.current = singleLocation[0]; return fetchGroupedTablePage('employeeTable', 0); } }
        : {}),
    };

    const fieldsToFetch = fields.filter((field) => fetchers[field] || paginatedFetchers[field]);

    setData((prev) => {
      const next = { ...prev };
      fieldsToFetch.forEach((field) => { (next as any)[field] = loading(); });
      return next;
    });

    const results = await Promise.allSettled(
      fieldsToFetch.filter((field) => fetchers[field]).map((field) => fetchers[field]!()),
    );

    const patch: Partial<CrewingApiData> = {};
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const [key, state] = result.value;
        (patch as any)[key] = state;
      }
    }

    setData((prev) => ({ ...prev, ...patch }));

    // Paginated fields update their own state progressively via onUpdate above — just await them
    // here (with per-field error handling) so fetchedTabsRef only marks the tab loaded once done.
    await Promise.allSettled(
      fieldsToFetch.filter((field) => paginatedFetchers[field]).map((field) =>
        paginatedFetchers[field]!().catch((e: any) => {
          setData((prev) => ({ ...prev, [field]: err(e?.message ?? 'Failed to load') }));
        }),
      ),
    );

    fetchedTabsRef.current.add(tab);
  }, [buildRequest, buildPriorRequest, fetchGroupedTablePage]);

  // Re-fetches a single grouped-table endpoint with an updated `groupDimensions` selection (from
  // the column picker), resetting its page back to 0 — a stale page number from before the column
  // change could otherwise be out of range for the new grouping's row count.
  //
  // Also clears the active sort if the column change made it invalid: either a dimension `sortBy`
  // that's no longer part of `groupDimensions` (e.g. sorting by Zone, then deselecting Zone), or
  // (hours/category-table only) a location-only metric (actualCases/routes/siteSqft) sortBy while
  // 'location' is no longer selected. The backend fails open on a stale sortBy (falls back to
  // default order) — this just keeps the UI's sort indicator from showing a stale/incorrect state.
  const refetchGroupedTable = useCallback((
    field: GroupedTableField,
    groupDimensions: string[],
  ) => {
    const current = tableSort[field];
    let sortOverride: TableSortState | undefined;
    if (current.sortBy) {
      const isDimensionSort = SORT_DIMENSION_KEYS[field].includes(current.sortBy);
      const isStaleDimensionSort = isDimensionSort && !groupDimensions.includes(current.sortBy);
      const isStaleLocationOnlyMetricSort =
        field === 'hoursCategoryTable' &&
        HOURS_CATEGORY_LOCATION_ONLY_SORT_KEYS.includes(current.sortBy) &&
        !groupDimensions.includes('location');
      if (isStaleDimensionSort || isStaleLocationOnlyMetricSort) {
        sortOverride = { ...DEFAULT_TABLE_SORT };
      }
    }
    return fetchGroupedTablePage(field, 0, groupDimensions, undefined, sortOverride);
  }, [fetchGroupedTablePage, tableSort]);

  const ensureTabLoaded = useCallback((tab: CrewingTabKey) => {
    if (!fetchedTabsRef.current.has(tab)) fetchTab(tab);
  }, [fetchTab]);

  const invalidateAll = useCallback(() => {
    fetchedTabsRef.current.clear();
  }, []);

  // Fetches the Employee Level Efficiency drilldown for a single location without touching the
  // global location filter/dropdown selection. `locationId` must be the backend Location_ID
  // (not the display name) — /efficiency/employee-table filters on Location_ID.
  const fetchEmployeeTableForLocation = useCallback((locationId: string) => {
    employeeTableLocationRef.current = locationId;
    // A newly-selected location resets back to the product default sort/page, not whatever was
    // left over from the previously-viewed location.
    return fetchGroupedTablePage('employeeTable', 0, undefined, undefined, { ...EMPLOYEE_TABLE_DEFAULT_SORT });
  }, [fetchGroupedTablePage]);

  const clearEmployeeTable = useCallback(() => {
    employeeTableLocationRef.current = null;
    setTablePages((prev) => ({ ...prev, employeeTable: 0 }));
    setTableSort((prev) => ({ ...prev, employeeTable: { ...EMPLOYEE_TABLE_DEFAULT_SORT } }));
    setData((prev) => ({ ...prev, employeeTable: idle() }));
  }, []);

  return {
    data,
    fetchTab,
    ensureTabLoaded,
    invalidateAll,
    refetchGroupedTable,
    fetchEmployeeTableForLocation,
    clearEmployeeTable,
    tablePages,
    tablePageSizes,
    setTablePageSize,
    goToTablePage,
    tableSort,
    toggleTableSort,
  };
}
